import React, { useState } from "react";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import {
  Download, Smartphone, Monitor, Share2, Plus, CheckCircle2, Info, MoreVertical, Apple, Rocket,
} from "lucide-react";
import { usePwaInstall } from "../hooks/usePwaInstall";

const PLATFORMS = [
  {
    key: "desktop", label: "No computador (Chrome ou Edge)", icon: Monitor,
    steps: [
      { icon: Download, text: "Clique no ícone de instalar (⊕) no canto direito da barra de endereço — ou use o botão “Instalar agora” acima quando aparecer." },
      { icon: CheckCircle2, text: "Confirme em “Instalar”. O BenDesk abre em janela própria e ganha um atalho na área de trabalho." },
    ],
  },
  {
    key: "android", label: "No Android (Chrome)", icon: Smartphone,
    steps: [
      { icon: MoreVertical, text: "Toque no menu (⋮) no canto superior direito do Chrome." },
      { icon: Plus, text: "Escolha “Instalar aplicativo” (ou “Adicionar à tela inicial”)." },
      { icon: CheckCircle2, text: "Confirme. O ícone do BenDesk aparece na tela inicial como um app." },
    ],
  },
  {
    key: "ios", label: "No iPhone ou iPad (Safari)", icon: Apple,
    steps: [
      { icon: Share2, text: "Toque no botão Compartilhar (o quadrado com a seta ↑) na barra do Safari." },
      { icon: Plus, text: "Role e toque em “Adicionar à Tela de Início”." },
      { icon: CheckCircle2, text: "Toque em “Adicionar”. O ícone do BenDesk vai pra tela inicial." },
    ],
  },
];

const Instalar = () => {
  const pwa = usePwaInstall();
  const [msg, setMsg] = useState("");

  const detected = pwa.isIOS ? "ios" : pwa.isAndroid ? "android" : "desktop";
  const ordered = [...PLATFORMS].sort(
    (a, b) => (a.key === detected ? -1 : 0) - (b.key === detected ? -1 : 0)
  );

  const handleInstall = async () => {
    const r = await pwa.promptInstall();
    if (r === "accepted") setMsg("Instalação iniciada! Confira a tela inicial do seu dispositivo.");
    else if (r === "dismissed") setMsg("Instalação cancelada — você pode instalar quando quiser.");
  };

  return (
    <Layout>
      <div className="max-w-4xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Instalar</h1>
          <p className="text-muted-foreground">
            Instale o BenDesk como aplicativo no seu computador e no seu celular.
          </p>
        </div>

        {pwa.isStandalone ? (
          <Card className="border-green-300 bg-green-50 dark:bg-green-950/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 shrink-0 text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-800 dark:text-green-300">
                    BenDesk já está instalado 🎉
                  </h3>
                  <p className="mt-1 text-sm text-green-700/80 dark:text-green-400/80">
                    Você está usando o app instalado neste dispositivo — em tela cheia e com atalho próprio.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Hero + botão de instalar */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary">
                    <Rocket className="h-7 w-7 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-semibold text-foreground">
                      Instale o BenDesk no seu dispositivo
                    </h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      Abre como um app de verdade: tela cheia, ícone na tela inicial e acesso rápido —
                      sem digitar o endereço toda vez. Funciona no computador e no celular.
                    </p>
                  </div>
                </div>

                {pwa.canInstall ? (
                  <div className="mt-5">
                    <Button onClick={handleInstall} className="w-full gap-2 sm:w-auto">
                      <Download className="h-4 w-4" /> Instalar agora
                    </Button>
                  </div>
                ) : (
                  <p className="mt-5 flex items-start gap-1.5 text-xs text-muted-foreground">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {pwa.isIOS
                      ? "No iPhone/iPad a instalação é pelo Safari, em poucos toques — siga o passo a passo abaixo."
                      : "Seu navegador instala pelo passo a passo abaixo. Dica: no computador use o Chrome ou o Edge para o botão de instalar aparecer."}
                  </p>
                )}
                {msg && <p className="mt-3 text-sm text-primary">{msg}</p>}
              </CardContent>
            </Card>

            {/* Passo a passo por dispositivo */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {ordered.map((p) => {
                const isDetected = p.key === detected;
                const Icon = p.icon;
                return (
                  <Card key={p.key} className={isDetected ? "border-primary/40" : ""}>
                    <CardContent className="pt-6">
                      <div className="mb-3 flex items-center gap-2">
                        <Icon className="h-5 w-5 shrink-0 text-primary" />
                        <h4 className="text-sm font-medium text-foreground">{p.label}</h4>
                        {isDetected && (
                          <span className="ml-auto whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
                            seu aparelho
                          </span>
                        )}
                      </div>
                      <ol className="space-y-2.5">
                        {p.steps.map((s, i) => {
                          const S = s.icon;
                          return (
                            <li key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                              <span className="flex shrink-0 items-center gap-1.5 text-primary/70">
                                <span className="w-4 text-right font-mono text-xs text-muted-foreground">{i + 1}.</span>
                                <S className="h-4 w-4" />
                              </span>
                              <span className="min-w-0">{s.text}</span>
                            </li>
                          );
                        })}
                      </ol>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Para instalar no celular de um gestor a partir do seu computador, peça para ele abrir
              <span className="mx-1 font-mono">app.bendesk.benfitech.com.br</span> no navegador do próprio
              celular e seguir o passo a passo do aparelho dele.
            </p>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Instalar;
